<template>
    <div v-if="show" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
      <div class="bg-white p-8 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-xl font-bold text-gray-800">Question Templates</h2>
          <button @click="$emit('close')" class="text-gray-600 hover:text-gray-800 focus:outline-none">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <div v-if="isLoading" class="flex-grow flex items-center justify-center">
          <div class="spinner"></div>
        </div>
        <div v-else-if="error" class="flex-grow flex items-center justify-center">
          <p class="text-red-600">{{ error }}</p>
        </div>
        <div v-else class="flex-grow overflow-y-auto" style="max-height: calc(90vh - 200px);">
          
          <div v-if="!isEditing" class="mb-4">
            <button @click="startNewTemplate" class="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition duration-300">
              Add New Template
            </button>
          </div>
          <div v-if="isEditing">
            <QuestionTemplateBuilder 
              :template="editingTemplate" 
              @save="saveTemplate"
              @cancel="cancelEdit"
            />
          </div>
          <div v-else>
            <h3 class="text-xl font-semibold mb-4">Existing Templates</h3>
            <div class="mb-4">
            <input
              v-model="searchQuery"
              @input="handleSearch"
              type="text"
              placeholder="Search question templates..."
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
            <ul class="space-y-4">
              <li v-for="template in paginatedTemplates" :key="template.id" class="bg-gray-100 p-4 rounded-lg">
                <div class="flex justify-between items-center">
                  <h4 class="text-lg font-medium">{{ template.name }}</h4>
                  <p class="text-gray-600">Description: {{ template.description }}</p>
                  <div>
                    <button @click="editTemplate(template)" class="text-blue-600 hover:text-blue-800 mr-2">Edit</button>
                    <button @click="deleteTemplate(template.id)" class="text-red-600 hover:text-red-800">Delete</button>
                  </div>
                </div>
                <p class="text-gray-600">Type: {{ template.type }}</p>
                <p class="text-gray-600">Question: {{ template.shortText }}</p>
              </li>
            </ul>
            <div v-if="filteredTemplates.length === 0" class="text-center py-4 text-gray-500">
              No question templates found. {{ searchQuery ? 'Try a different search term or c' : 'C' }}lick "Add New Template" to create one.
            </div>
            <div v-else class="mt-4 flex justify-between items-center">
              <span class="text-sm text-gray-600">
                Showing {{ startIndex + 1 }} to {{ endIndex }} of {{ filteredTemplates.length }} templates
              </span>
              <div class="space-x-2">
                <button 
                  @click="prevPage" 
                  :disabled="currentPage === 1"
                  class="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
                  :class="{ 'hover:bg-gray-100': currentPage !== 1 }"
                >
                  Previous
                </button>
                <button 
                  @click="nextPage" 
                  :disabled="currentPage === totalPages"
                  class="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
                  :class="{ 'hover:bg-gray-100': currentPage !== totalPages }"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import QuestionTemplateBuilder from '../QuestionTemplateBuilder.vue'
  import { questionTemplateApi } from '@/api/questionTemplateApi'
  
  export default {
    name: 'QuestionTemplateModal',
    components: {
      QuestionTemplateBuilder
    },
    props: {
      show: {
        type: Boolean,
        required: true
      }
    },
    data() {
      return {
        templates: [],
        isEditing: false,
        editingTemplate: null,
        currentPage: 1,
        itemsPerPage: 5,
        isLoading: false,
        error: null,
        searchQuery: '',
      }
    },
    computed: {
      filteredTemplates() {
        return this.templates.filter(template =>
          template.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          template.text.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
      },
      paginatedTemplates() {
        const start = (this.currentPage - 1) * this.itemsPerPage;
        const end = start + this.itemsPerPage;
        return this.shortenedTemplates(this.filteredTemplates).slice(start, end);
      },
      totalPages() {
        return Math.ceil(this.filteredTemplates.length / this.itemsPerPage);
      },
      startIndex() {
        return (this.currentPage - 1) * this.itemsPerPage;
      },
      endIndex() {
        return Math.min(this.startIndex + this.itemsPerPage, this.filteredTemplates.length);
      },
      shortenedTemplates() {
        return (templates) => templates.map(template => ({
          ...template,
          shortText: template.text.length > 50 ? template.text.substring(0, 47) + '...' : template.text,
          shortDescription: template.description && template.description.length > 100 ? template.description.substring(0, 97) + '...' : template.description
        }));
      }
    },
    methods: {
      async fetchTemplates() {
        this.isLoading = true;
        this.error = null;
        try {
          const templates = await questionTemplateApi.getAll();
          this.templates = templates;
        } catch (error) {
          console.error('Error fetching templates:', error);
          this.error = 'Failed to fetch templates. Please try again.';
        } finally {
          this.isLoading = false;
        }
      },
      startNewTemplate() {
        this.editingTemplate = {
          id: '',
          name: '',
          description: '',
          type: 'single',
          text: '',
          options: [''],
          maxRating: 5
        }
        this.isEditing = true
      },
      editTemplate(template) {
        this.editingTemplate = { ...template }
        this.isEditing = true
      },
      async saveTemplate(template) {
        this.isLoading = true;
        this.error = null;
        try {
          if (template.id) {
            await questionTemplateApi.update(template.id, template);
          } else {
            await questionTemplateApi.create(template);
          }
          await this.fetchTemplates();
          this.isEditing = false;
          this.editingTemplate = null;
        } catch (error) {
          console.error('Error saving template:', error);
          this.error = 'Failed to save template. Please try again.';
        } finally {
          this.isLoading = false;
        }
      },
      async deleteTemplate(id) {
        if (confirm('Are you sure you want to delete this template?')) {
          this.isLoading = true;
          this.error = null;
          try {
            await questionTemplateApi.delete(id);
            await this.fetchTemplates();
            if (this.paginatedTemplates.length === 0 && this.currentPage > 1) {
              this.currentPage--;
            }
          } catch (error) {
            console.error('Error deleting template:', error);
            this.error = 'Failed to delete template. Please try again.';
          } finally {
            this.isLoading = false;
          }
        }
      },
      cancelEdit() {
        this.isEditing = false
        this.editingTemplate = null
      },
      prevPage() {
        if (this.currentPage > 1) {
          this.currentPage--
        }
      },
      nextPage() {
        if (this.currentPage < this.totalPages) {
          this.currentPage++
        }
      },
      handleSearch() {
        this.currentPage = 1;
      }
    },
    mounted() {
      this.fetchTemplates();
    }
  }
  </script>
  
  <style scoped>
  .spinner {
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  </style>
  
  